<template>
  <div class="ocalc">
    <p v-if="!items.length" class="ocalc-hint">{{ $t("offerCalculation.noItems") }}</p>
    <p v-else-if="error" class="ocalc-hint ocalc-error">{{ error }}</p>
    <div v-else-if="!calc" class="ocalc-hint">{{ $t("offerCalculation.loading") }}</div>
    <template v-else>
      <div v-if="shownNotes.length" class="ocalc-notes">
        <div v-for="n in shownNotes" :key="n">
          <md-icon>info</md-icon> {{ noteText(n) }}
        </div>
      </div>

      <table class="ocalc-table">
        <thead>
          <tr>
            <th></th>
            <th></th>
            <th class="ocalc-amount">€</th>
            <th class="ocalc-manual">{{ $t("offerCalculation.manual") }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="calc.purchasePriceEur != null">
            <tr class="ocalc-head calc-row calc-row--a calc-head">
              <calc-block-cell block="a" :rows="blockARows" />
              <td><calc-label :short="$t('calcLabels.ek')" :name="$t('calcLabels.ekName')" :extra="calc.exchangeRate != null ? $t('calcLabels.rate', { rate: fmt(calc.exchangeRate, 3) }) : ''" :tip="$t('offerCalculation.tip.purchaseUsd')" /></td>
              <td class="ocalc-amount">{{ fmt(calc.purchasePriceUsd) }} USD</td>
              <td class="ocalc-manual"><last-purchase-refresh @refreshed="run" /></td>
            </tr>
            <tr class="calc-row calc-row--a">
              <td><calc-label prefix="=" plain :short="$t('calcLabels.ek')" :name="$t('calcLabels.ekName')" :tip="$t('offerCalculation.tip.purchaseEur')" /></td>
              <td class="ocalc-amount">{{ eur(calc.purchasePriceEur) }}</td>
              <td class="ocalc-manual"></td>
            </tr>
            <tr class="calc-row calc-row--a">
              <td><calc-label prefix="+" plain :short="$t('calcLabels.bznk')" :name="$t('calcLabels.bznkName')" :tip="$t('offerCalculation.tip.bznk')" /></td>
              <td class="ocalc-amount">{{ eur(calc.bznk) }}</td>
              <td class="ocalc-manual"></td>
            </tr>
            <tr class="calc-row calc-row--a">
              <td><calc-label prefix="+" :name="$t('calcLabels.customs')" :extra="$t('calcLabels.customsRate', { rate: pct(calc.customsRatePercent, 1) })" :tip="$t('offerCalculation.tip.customs')" /></td>
              <td class="ocalc-amount">{{ eur(calc.customs) }}</td>
              <td class="ocalc-manual"></td>
            </tr>
          </template>
          <tr class="ocalc-sum calc-row calc-row--a calc-sum">
            <calc-block-cell v-if="calc.purchasePriceEur == null" block="a" :rows="1" />
            <td><calc-label :prefix="calc.purchasePriceEur != null ? '=' : ''" short="BEP" :name="$t('calcLabels.bepName')" :tip="$t('offerCalculation.tip.bep')" /></td>
            <td class="ocalc-amount">{{ eur(calc.bep) }}</td>
            <td class="ocalc-manual"><last-purchase-refresh v-if="calc.purchasePriceEur == null" @refreshed="run" /></td>
          </tr>
          <tr class="calc-row calc-row--b">
            <calc-block-cell block="b" :rows="8" />
            <td><calc-label prefix="+" :name="$t('offerCalculation.containerUnloading')" :tip="$t('offerCalculation.tip.containerUnloading')" /></td>
            <td class="ocalc-amount">{{ eur(calc.containerUnloading) }}</td>
            <td class="ocalc-manual"></td>
          </tr>
          <tr class="calc-row calc-row--b">
            <td><calc-label prefix="+" :name="$t('offerCalculation.storage')" :tip="$t('offerCalculation.tip.storage')" /></td>
            <td class="ocalc-amount">{{ eur(calc.storage) }}</td>
            <td class="ocalc-manual"></td>
          </tr>
          <tr class="calc-row calc-row--b">
            <td><calc-label prefix="+" :name="$t('offerCalculation.additional')" :tip="$t('offerCalculation.tip.additional')" /></td>
            <td class="ocalc-amount">{{ eur(calc.additional) }}</td>
            <td class="ocalc-manual"></td>
          </tr>
          <tr class="ocalc-sum calc-row calc-row--b calc-sum">
            <td><calc-label prefix="=" short="LAP" :name="$t('calcLabels.lapName')" :tip="$t('offerCalculation.tip.lap')" /></td>
            <td class="ocalc-amount">{{ eur(calc.lap) }}</td>
            <td class="ocalc-manual"></td>
          </tr>
          <tr class="calc-row calc-row--b">
            <td><calc-label prefix="+" :name="$t('offerCalculation.pick')" :tip="$t('offerCalculation.tip.pick')" />
              <div v-if="explain('pick')" class="ocalc-explain">{{ explain('pick') }} <a class="ocalc-reset" @click.prevent="resetField('pick')">{{ $t("offerCalculation.reset") }}</a></div></td>
            <td class="ocalc-amount">{{ eur(calc.pick) }}</td>
            <td class="ocalc-manual"><span class="ocalc-ov"><input type="number" min="0" step="0.01" :value="overrides['pickCost']" :placeholder="autoText(calc.automatic.pick)" :class="{ 'ocalc-ov--set': hasOverride('pickCost') }" @input="$emit('override', 'pickCost', $event.target.value)" /><span class="ocalc-unit">€</span></span></td>
          </tr>
          <tr class="calc-row calc-row--b">
            <td><calc-label prefix="+" :name="$t('offerCalculation.provisioning')" :tip="$t('offerCalculation.tip.provisioning')" /></td>
            <td class="ocalc-amount">{{ eur(calc.provisioning) }}</td>
            <td class="ocalc-manual"></td>
          </tr>
          <tr class="calc-row calc-row--b">
            <td><calc-label prefix="+" :name="$t('offerCalculation.shipping')" :tip="$t('offerCalculation.tip.shipping')" />
              <div v-if="explain('shipping')" class="ocalc-explain">{{ explain('shipping') }} <a class="ocalc-reset" @click.prevent="resetField('shipping')">{{ $t("offerCalculation.reset") }}</a></div></td>
            <td class="ocalc-amount">{{ eur(calc.shipping) }}</td>
            <td class="ocalc-manual"><span class="ocalc-ov"><input type="number" min="0" step="0.01" :value="overrides['shippingCost']" :placeholder="autoText(calc.automatic.shipping)" :class="{ 'ocalc-ov--set': hasOverride('shippingCost') }" @input="$emit('override', 'shippingCost', $event.target.value)" /><span class="ocalc-unit">€</span></span></td>
          </tr>
          <tr class="ocalc-sum calc-row calc-row--b calc-sum">
            <td><calc-label prefix="=" short="BSP" :name="$t('calcLabels.bspName')" :tip="$t('offerCalculation.tip.bsp')" /></td>
            <td class="ocalc-amount">{{ eur(calc.bsp) }}</td>
            <td class="ocalc-manual"></td>
          </tr>
          <tr class="calc-row calc-row--c">
            <calc-block-cell block="c" :rows="2" />
            <td><calc-label prefix="+" :name="$t('offerCalculation.fixedCost')" :extra="pct(calc.fixedCostSharePercent)" :tip="$t('offerCalculation.tip.fixedCost')" />
              <div v-if="explain('fixedCost')" class="ocalc-explain">{{ explain('fixedCost') }} <a class="ocalc-reset" @click.prevent="resetField('fixedCost')">{{ $t("offerCalculation.reset") }}</a></div></td>
            <td class="ocalc-amount">{{ eur(calc.fixedCost) }}</td>
            <td class="ocalc-manual"><span class="ocalc-ov"><input type="number" min="0" step="0.01" :value="overrides['fixedCostSharePercent']" :placeholder="autoText(calc.automatic.fixedCostSharePercent)" :class="{ 'ocalc-ov--set': hasOverride('fixedCostSharePercent') }" @input="$emit('override', 'fixedCostSharePercent', $event.target.value)" /><span class="ocalc-unit">%</span></span></td>
          </tr>
          <tr class="ocalc-sum calc-row calc-row--c calc-sum">
            <td><calc-label prefix="=" short="BKP" :name="$t('calcLabels.bkpName')" :tip="$t('offerCalculation.tip.bkp')" /></td>
            <td class="ocalc-amount">{{ eur(calc.bkp) }}</td>
            <td class="ocalc-manual"></td>
          </tr>
          <tr class="calc-row calc-row--d">
            <calc-block-cell block="d" :rows="4" />
            <td><calc-label prefix="+" :name="$t('offerCalculation.returnCost')" :extra="$t('calcLabels.returnRate', { rate: pct(calc.returnRatePercent, 1) })" :tip="$t('offerCalculation.tip.returnCost')" />
              <div v-if="explain('returnCost')" class="ocalc-explain">{{ explain('returnCost') }} <a class="ocalc-reset" @click.prevent="resetField('returnCost')">{{ $t("offerCalculation.reset") }}</a></div></td>
            <td class="ocalc-amount">{{ eur(calc.returnCost) }}</td>
            <td class="ocalc-manual"><span class="ocalc-ov"><input type="number" min="0" step="0.01" :value="overrides['returnRatePercent']" :placeholder="autoText(calc.automatic.returnRatePercent)" :class="{ 'ocalc-ov--set': hasOverride('returnRatePercent') }" @input="$emit('override', 'returnRatePercent', $event.target.value)" /><span class="ocalc-unit">%</span></span></td>
          </tr>
          <tr class="calc-row calc-row--d">
            <td><calc-label prefix="+" :name="$t('offerCalculation.marketing')" :extra="$t('calcLabels.tacos', { rate: pct(calc.acosPercent, 1) })" :tip="$t('offerCalculation.tip.marketing')" />
              <div v-if="explain('marketing')" class="ocalc-explain">{{ explain('marketing') }} <a class="ocalc-reset" @click.prevent="resetField('marketing')">{{ $t("offerCalculation.reset") }}</a></div></td>
            <td class="ocalc-amount">{{ eur(calc.marketing) }}</td>
            <td class="ocalc-manual"><span class="ocalc-ov"><input type="number" min="0" step="0.01" :value="overrides['acosPercent']" :placeholder="autoText(calc.automatic.acosPercent)" :class="{ 'ocalc-ov--set': hasOverride('acosPercent') }" @input="$emit('override', 'acosPercent', $event.target.value)" /><span class="ocalc-unit">%</span></span></td>
          </tr>
          <tr class="calc-row calc-row--d">
            <td><calc-label prefix="+" :name="$t('calcLabels.commission')" :extra="$t('calcLabels.fee', { rate: pct(calc.marketplaceFeePercent, 1) })" :tip="$t('offerCalculation.tip.commission')" /></td>
            <td class="ocalc-amount">{{ eur(calc.commission) }}</td>
            <td class="ocalc-manual"></td>
          </tr>
          <tr class="ocalc-sum calc-row calc-row--d calc-sum">
            <td><calc-label prefix="=" short="SKP" :name="$t('calcLabels.skpName')" :tip="$t('offerCalculation.tip.skp')" /></td>
            <td class="ocalc-amount">{{ eur(calc.skp) }}</td>
            <td class="ocalc-manual"></td>
          </tr>
          <tr class="ocalc-gap"><td colspan="4"></td></tr>
          <tr class="ocalc-profit" :class="profitClass">
            <td></td>
            <td>{{ $t("offerCalculation.profit") }} <help-tip :text="$t('offerCalculation.tip.profit')"></help-tip></td>
            <td class="ocalc-amount">{{ eur(calc.profit) }}</td>
            <td class="ocalc-manual"></td>
          </tr>
          <tr class="ocalc-gap"><td colspan="4"></td></tr>
          <tr class="ocalc-sum calc-row calc-row--d calc-sum">
            <calc-block-cell block="d" :rows="3" compact />
            <td><calc-label :short="$t('calcLabels.vk')" :name="$t('calcLabels.vkName')" :extra="$t('calcLabels.net')" :tip="$t('offerCalculation.tip.saleNet')" /></td>
            <td class="ocalc-amount">{{ eur(calc.saleNet) }}</td>
            <td class="ocalc-manual"></td>
          </tr>
          <tr class="calc-row calc-row--d">
            <td><calc-label :name="$t('calcLabels.vat')" :extra="pct(calc.vatPercent, 0)" :tip="$t('offerCalculation.tip.vat')" />
              <div v-if="explain('vat')" class="ocalc-explain">{{ explain('vat') }} <a class="ocalc-reset" @click.prevent="resetField('vat')">{{ $t("offerCalculation.reset") }}</a></div></td>
            <td class="ocalc-amount">{{ eur(calc.vat) }}</td>
            <td class="ocalc-manual"><span class="ocalc-ov"><input type="number" min="0" step="0.01" :value="overrides['vatPercent']" :placeholder="autoText(calc.automatic.vatPercent)" :class="{ 'ocalc-ov--set': hasOverride('vatPercent') }" @input="$emit('override', 'vatPercent', $event.target.value)" /><span class="ocalc-unit">%</span></span></td>
          </tr>
          <tr class="ocalc-sum calc-row calc-row--d calc-sum">
            <td><calc-label :short="$t('calcLabels.vk')" :name="$t('calcLabels.vkName')" :extra="$t('calcLabels.gross')" :tip="$t('offerCalculation.tip.saleGross')" />
</td>
            <td class="ocalc-amount">{{ eur(calc.saleGross) }}</td>
            <td class="ocalc-manual"></td>
          </tr>
        </tbody>
      </table>

      <h5 class="ocalc-section">{{ $t("offerCalculation.figures") }}</h5>
      <table class="ocalc-table">
        <thead>
          <tr>
            <th></th>
            <th class="ocalc-amount">€</th>
            <th class="ocalc-amount">% {{ $t("offerCalculation.ofNet") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="f in figures" :key="f.key" :title="$t('offerCalculation.rule.' + f.key)">
            <td>{{ f.label }} <help-tip :text="$t('offerCalculation.rule.' + f.key)"></help-tip></td>
            <td class="ocalc-amount" :class="signClass(f.value)">{{ eur(f.value) }}</td>
            <td class="ocalc-amount" :class="signClass(f.percent)">{{ pct(f.percent) }}</td>
          </tr>
        </tbody>
      </table>
      <h5 class="ocalc-section">{{ $t("offerCalculation.floorTitle") }}</h5>
      <table class="ocalc-table">
        <thead>
          <tr>
            <th></th>
            <th class="ocalc-amount">{{ $t("offerCalculation.saleGross") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="f in floors" :key="f.key">
            <td><calc-label :short="f.short" :name="$t('offerCalculation.floor.' + f.key)" :tip="floorTip(f)"></calc-label></td>
            <td class="ocalc-amount" :class="floorClass(f.gross)">{{ euroUp(f.gross) }}</td>
          </tr>
        </tbody>
      </table>
      <p class="ocalc-hint">{{ $t("offerCalculation.floorHint") }}</p>
      <template v-if="shippingLines.length">
        <h5 class="ocalc-section">{{ $t("offerCalculation.shippingDetail.title") }} <help-tip :text="$t('offerCalculation.shippingDetail.tip')"></help-tip></h5>
        <p v-if="(calc.overridden || []).includes('shipping')" class="ocalc-hint ocalc-hint--warn">{{ $t("offerCalculation.shippingDetail.overridden") }}</p>
        <table class="ocalc-table ocalc-ship-table">
          <thead>
            <tr>
              <th>{{ $t("offerCalculation.shippingDetail.product") }}</th>
              <th class="ocalc-amount">{{ $t("offerCalculation.shippingDetail.quantity") }}</th>
              <th class="ocalc-amount">{{ $t("offerCalculation.shippingDetail.parcels") }}</th>
              <th>{{ $t("offerCalculation.shippingDetail.parcelSize") }}</th>
              <th>{{ $t("offerCalculation.shippingDetail.carrier") }}</th>
              <th class="ocalc-amount">{{ $t("offerCalculation.shippingDetail.perParcel") }}</th>
              <th class="ocalc-amount">{{ $t("offerCalculation.shippingDetail.total") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="l in shippingLines" :key="l.productId">
              <td>{{ l.sku }}</td>
              <td class="ocalc-amount">{{ l.quantity }}</td>
              <td class="ocalc-amount">
                <template v-if="l.parcels != null">
                  {{ l.parcels }}
                  <small v-if="l.combined" class="ocalc-ship-sub">{{ $t("offerCalculation.shippingDetail.piecesEach", { n: l.piecesPerParcel }) }}</small>
                </template>
                <template v-else>–</template>
              </td>
              <td>{{ parcelSize(l) }}</td>
              <td>{{ l.outboundCarrier || "–" }}</td>
              <td class="ocalc-amount">{{ l.costPerParcel == null ? "–" : fmt(l.costPerParcel) + " €" }}</td>
              <td class="ocalc-amount">
                <template v-if="l.cost != null">{{ fmt(l.cost) }} €</template>
                <template v-else>
                  <span :title="reasonText(l)">–</span>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
        <details v-for="l in linesWithCarriers" :key="'d' + l.productId" class="ocalc-ship-detail" :open="linesWithCarriers.length === 1">
          <summary>{{ $t("offerCalculation.shippingDetail.carriersFor", { sku: l.sku, size: parcelSize(l) }) }}</summary>
          <parcel-carrier-cards :carriers="l.carriers" :used-carrier="l.outboundCarrier" :used-label="$t('productCalculation.usedCarrier')"></parcel-carrier-cards>
        </details>
      </template>
      <p class="ocalc-hint">{{ $t("offerCalculation.weight") }}: {{ calc.weightKg == null ? "–" : fmt(calc.weightKg, 3) + " kg" }}</p>
    </template>
  </div>
</template>

<script>
import CalcBlockCell from "@/components/Calculation/CalcBlockCell.vue";
import CalcLabel from "@/components/Calculation/CalcLabel.vue";
import HelpTip from "@/components/HelpTip.vue";
import LastPurchaseRefresh from "@/components/Calculation/LastPurchaseRefresh.vue";
import ParcelCarrierCards from "@/components/ParcelCarrierCards.vue";
import offersService from "@/services/offers.service";

export default {
  components: { CalcBlockCell, CalcLabel, HelpTip, LastPurchaseRefresh, ParcelCarrierCards },
  props: {
    sku: { type: String, default: "" },
    items: { type: Array, required: true },
    amounts: { type: Object, required: true },
    overrides: { type: Object, required: true },
  },
  data() {
    return { calc: null, error: "", timer: null, requestId: 0 };
  },
  computed: {
    // Block A Wareneingang: mit EK-Zeilen (USD, EUR, BZNK, Zoll) plus BEP, sonst nur BEP
    blockARows() {
      return this.calc && this.calc.purchasePriceEur != null ? 5 : 1;
    },
    shippingLines() {
      return (this.calc && this.calc.shippingLines) || [];
    },
    // Paketdienst-Vergleich nur fuer Positionen mit Preisen; Spedition oder fehlende Masse haben keinen
    linesWithCarriers() {
      return this.shippingLines.filter((l) => l.carriers && l.carriers.length && l.parcels != null);
    },
    payload() {
      const num = (v) => (v === "" || v == null || !Number.isFinite(Number(v)) ? null : Number(v));
      return {
        sku: this.sku.trim() || null,
        items: this.items.map((i) => ({ productId: i.productId, quantity: Number(i.quantity) || 0 })),
        handlingCost: num(this.amounts.handlingCost),
        commissionClass: this.amounts.commissionClass || null,
        pickCostOverride: num(this.overrides.pickCost),
        shippingCostOverride: num(this.overrides.shippingCost),
        acosPercentOverride: num(this.overrides.acosPercent),
        fixedCostSharePercentOverride: num(this.overrides.fixedCostSharePercent),
        returnRatePercentOverride: num(this.overrides.returnRatePercent),
        vatPercentOverride: num(this.overrides.vatPercent),
      };
    },
    payloadKey() {
      return JSON.stringify(this.payload);
    },
    profitClass() {
      if (!this.calc || this.calc.profit == null) return "";
      return this.calc.profit >= 0 ? "ocalc-profit--plus" : "ocalc-profit--minus";
    },
    shownNotes() {
      // Der VK kommt immer aus Afterbuy (Trello KWDTD2pU, Update a); der Hinweis darauf ist ueberfluessig
      return this.calc.notes.filter((n) => !n.startsWith("override_") && !n.startsWith("price_from_history"));
    },
    floors() {
      const c = this.calc;
      return [
        { key: "db1", short: "KPU", db: "DB1", gross: c.floorDb1Gross },
        { key: "db2", short: "OPU", db: "DB2", gross: c.floorDb2Gross },
        { key: "db3", short: "LPU", db: "DB3", gross: c.floorDb3Gross },
      ];
    },
    figures() {
      const c = this.calc;
      return [
        { key: "grossProfit", label: this.$t("offerCalculation.grossProfit"), value: c.grossProfit, percent: c.grossProfitPercent },
        { key: "db1", label: "DB1", value: c.db1, percent: c.db1Percent },
        { key: "db2", label: "DB2", value: c.db2, percent: c.db2Percent },
        { key: "db3", label: "DB3", value: c.db3, percent: c.db3Percent },
      ];
    },
  },
  watch: {
    payloadKey() {
      this.schedule();
    },
  },
  created() {
    this.run();
  },
  beforeDestroy() {
    clearTimeout(this.timer);
  },
  methods: {
    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(this.run, 500);
    },
    run() {
      if (!this.items.length) return Promise.resolve();
      const requestId = ++this.requestId;
      return offersService
        .calculateOffer(this.payload)
        .then((calc) => {
          if (requestId !== this.requestId) return;
          this.calc = calc;
          this.error = "";
        })
        .catch((e) => {
          if (requestId !== this.requestId) return;
          const reason = e && e.response && e.response.data && e.response.data.reason;
          this.error = reason && this.$te(`offers.errors.${reason}`) ? this.$t(`offers.errors.${reason}`) : this.$t("offerCalculation.failed");
        });
    },
    // Preisuntergrenze auf den vollen Cent aufgerundet, damit der Preis den Deckungsbeitrag sicher erreicht
    parcelSize(l) {
      if (l.lengthCm == null || l.widthCm == null || l.heightCm == null) return "–";
      const n = (v) => this.fmt(v, 1);
      return `${n(l.lengthCm)} × ${n(l.widthCm)} × ${n(l.heightCm)} cm · ${this.fmt(l.weightKg, 2)} kg`;
    },
    reasonText(l) {
      return l.unavailableReason ? this.$t("productCalculation.shippingReason." + l.unavailableReason) : "";
    },
    euroUp(value) {
      return value == null ? "–" : `${this.fmt(Math.ceil(value * 100 - 1e-9) / 100)} €`;
    },
    // "KPU = DB1 = 0,00 €" und darunter, welche Kosten in die Grenze eingehen
    floorTip(f) {
      return `${f.short} = ${f.db} = ${this.fmt(0)} €\n${this.$t("offerCalculation.floorTip." + f.key)}`;
    },
    // gruen, wenn der eingegebene Brutto-VK die Untergrenze erreicht; rot, wenn er darunter liegt
    floorClass(floor) {
      const gross = this.calc && this.calc.saleGross;
      if (floor == null || gross == null) return "";
      return gross + 0.005 >= floor ? "ocalc-plus" : "ocalc-minus";
    },
    // Erklaerung, wenn ein manueller Eintrag eine Automatik ersetzt (leer, solange die Automatik gilt)
    explain(key) {
      const c = this.calc;
      if (!c) return "";
      const rules = {
        pick: { value: c.automatic && c.automatic.pick, unit: "eur" },
        shipping: { value: c.automatic && c.automatic.shipping, unit: "eur" },
        fixedCost: { value: c.automatic && c.automatic.fixedCostSharePercent, unit: "pct" },
        returnCost: { value: c.automatic && c.automatic.returnRatePercent, unit: "pct" },
        marketing: { value: c.automatic && c.automatic.acosPercent, unit: "pct" },
        vat: { value: c.automatic && c.automatic.vatPercent, unit: "pct" },
      };
      const auto = (value, unit) => (value == null ? this.$t("offerCalculation.explain.autoOpen") : unit === "eur" ? this.eur(value) : this.pct(value));
      if (rules[key]) {
        if (!(c.overridden || []).includes(key)) return "";
        return this.$t("offerCalculation.explain.override", { rule: this.$t(`offerCalculation.autoRule.${key}`), auto: auto(rules[key].value, rules[key].unit) });
      }
      return "";
    },
    resetField(key) {
      const overrides = { pick: "pickCost", shipping: "shippingCost", fixedCost: "fixedCostSharePercent", returnCost: "returnRatePercent", marketing: "acosPercent", vat: "vatPercent" };
      if (overrides[key]) this.$emit("override", overrides[key], "");
    },
    hasOverride(key) {
      const v = this.overrides[key];
      return v !== "" && v != null;
    },
    // automatischer Wert als Platzhalter des manuellen Feldes
    autoText(value) {
      return value == null ? "" : this.fmt(value);
    },
    signClass(value) {
      if (value == null) return "";
      return value >= 0 ? "ocalc-plus" : "ocalc-minus";
    },
    noteText(code) {
      const lines = (this.calc.itemNotes || []).filter((n) => n.code === code);
      const skus = lines.map((n) => {
        if (code !== "shipping_combined" || !n.detail) return n.sku;
        const [quantity, parcels] = n.detail.split(":");
        return `${n.sku}: ${this.$t("offerCalculation.combinedDetail", { quantity, parcels })}`;
      });
      const params = { date: this.calc.priceDate ? new Date(this.calc.priceDate).toLocaleDateString(this.$i18n.locale) : "" };
      const text = this.$te(`offerCalculation.notes.${code}`) ? this.$t(`offerCalculation.notes.${code}`, params) : code;
      return skus.length ? `${text} (${skus.join(", ")})` : text;
    },
    fmt(value, decimals = 2) {
      return this.$options.filters.formatNumberGerman(value, decimals);
    },
    eur(value) {
      return value == null ? "–" : `${this.fmt(value)} €`;
    },
    pct(value, decimals = 2) {
      return value == null ? "–" : `${this.fmt(value, decimals)} %`;
    },
  },
};
</script>

<style scoped>
.ocalc {
  text-align: left;
}
.ocalc-hint {
  color: #777;
  font-size: 12px;
  margin: 8px 0;
}
.ocalc-error {
  color: #f44336;
}
.ocalc-notes {
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 4px;
  padding: 8px 10px;
  margin-bottom: 10px;
  font-size: 12px;
  color: #8a5a00;
}
.ocalc-notes .md-icon {
  color: #ff9800 !important;
  font-size: 16px !important;
  min-width: 16px;
  width: 16px;
  height: 16px;
  vertical-align: text-bottom;
}
.ocalc-section {
  margin: 18px 0 6px;
  font-weight: 500;
}
.ocalc-table {
  width: 100%;
  border-collapse: collapse;
}
/* Die globale Regel ".md-tabs-content table thead" blendet Tabellenkoepfe in Tabs aus */
.ocalc-table thead {
  display: table-header-group;
}
.ocalc-table th {
  font-size: 12px;
  font-weight: 500;
  color: #7f915a;
  padding: 4px 6px;
  border-bottom: 1px solid #ddd;
}
.ocalc-table td {
  padding: 4px 6px;
  border-bottom: 1px solid #eee;
}
.ocalc-amount {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  width: 110px;
}
.ocalc-plus {
  color: #7f915a;
  font-weight: 600;
}
.ocalc-minus {
  color: #c62828;
  font-weight: 600;
}
.ocalc-manual {
  width: 104px;
  text-align: right;
}
.ocalc-ov {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.ocalc-ov input {
  width: 72px;
  padding: 2px 4px;
  border: 1px solid #ddd;
  border-radius: 3px;
  text-align: right;
  font-size: 12px;
}
.ocalc-ov input::placeholder {
  color: #aaa;
}
.ocalc-ov--set {
  border-color: #ff9800 !important;
  background: #fff8e1;
}
.ocalc-unit {
  color: #888;
  font-size: 11px;
  min-width: 10px;
}
.ocalc-explain {
  margin-top: 2px;
  font-size: 11px;
  line-height: 1.3;
  color: #e65100;
}
.ocalc-reset {
  margin-left: 4px;
  color: #1976d2;
  cursor: pointer;
  text-decoration: underline;
}
.ocalc-sum td {
  border-top: 2px solid #ddd;
}
.ocalc-sum .ocalc-amount,
.ocalc-head .ocalc-amount {
  font-weight: 700;
}
.ocalc-profit td {
  font-weight: 700;
  font-size: 15px;
}
.ocalc-gap td {
  height: 14px;
  padding: 0 !important;
  background: #fff;
  border: none !important;
}
.ocalc-profit--plus td {
  background: #f1f5e8;
  color: #7f915a;
}
.ocalc-profit--minus td {
  background: #ffebee;
  color: #c62828;
}
.ocalc-hint--warn {
  color: #b26a00;
}
.ocalc-ship-table td,
.ocalc-ship-table th {
  white-space: nowrap;
}
.ocalc-ship-sub {
  display: block;
  color: #888;
  font-size: 11px;
}
.ocalc-ship-detail {
  margin-top: 10px;
}
.ocalc-ship-detail summary {
  cursor: pointer;
  font-size: 12px;
  color: #555;
  margin-bottom: 8px;
}
</style>
